import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Above this summary/drawer height ratio the summary stops sticking (a huge summary
   * would swallow the drawer). Measured on phones: a 3-line cart on an iPhone 13 hits
   * 0.501 and a 1-line cart on an SE hits 0.602 — at 0.5 both un-stuck and pushed
   * "Check out" below the fold (253px down / clipped off-screen). 0.65 keeps the
   * summary pinned in every measured case while still bailing on truly oversized ones.
   * @type {number}
   */
  #summaryThreshold = 0.65;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {string | null} */
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleEscapeOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleEscapeClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleEscapeOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleEscapeClose);
    this.#historyAbortController?.abort();
    this.#handleEscapeClose();
  }

  /**
   * Escape must close the drawer on the FIRST press wherever focus sits. Measured:
   * once the user had tabbed into the drawer, the first Esc only snapped focus to the
   * Close button (an inner handler swallowed it before the component's own bubble
   * listener) and a second press was needed — a keyboard user appeared trapped.
   * A capture-phase document listener fires ahead of any inner handler.
   */
  #handleEscapeOpen = () => {
    document.addEventListener('keydown', this.#handleEscapeKeydown, { capture: true });
  };

  #handleEscapeClose = () => {
    document.removeEventListener('keydown', this.#handleEscapeKeydown, { capture: true });
  };

  /** @param {KeyboardEvent} event */
  #handleEscapeKeydown = (event) => {
    if (event.key !== 'Escape' || !this.refs.dialog?.open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.closeDialog();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // Manual scroll restoration for the drawer's history entry: the browser snapshots
      // the page's scroll while the body is position-fixed (i.e. 0), and the closing
      // history.back() then restored THAT — traced: closeDialog put the page back at
      // 1400 and the back() immediately snapped it to 0, destroying reading context on
      // every mobile add-to-cart.
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    // Hand scroll restoration back to the browser once our synthetic entry is gone
    // (after the back() above has been processed).
    const prev = this.#previousScrollRestoration;
    if (prev) {
      setTimeout(() => {
        history.scrollRestoration = prev;
      }, 100);
      this.#previousScrollRestoration = null;
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
